// =========================================================================
// Calendar module service layer.
//
// Backs @pc/modules-calendar with:
//   * Keyset-paginated appointment agenda for the active store (or org).
//   * Per-appointment CRUD via SECURITY DEFINER RPCs.
//   * Staff-contact directory (external staff w/o auth.users).
//   * Per-org settings.
//   * Subscribable feed token mint/revoke.
//
// Schema lives at supabase/migrations/20260613000200_calendar_init.sql.
// We declare local row interfaces because the generated `Database` type
// regenerates at deploy time and doesn't yet include the calendar schema.
// =========================================================================

import { supabase } from '../client';
import { requireOrgContext, type ActiveContext } from '../activeContext';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';

// ---- types --------------------------------------------------------------

export type AppointmentScope = 'org' | 'store' | 'user';
export type AppointmentType = 'in_person' | 'phone' | 'video' | 'other';
export type AppointmentStatus =
  | 'scheduled'
  | 'confirmed'
  | 'cancelled'
  | 'completed'
  | 'no_show';
export type ConfirmationState =
  | 'not_required'
  | 'awaiting'
  | 'confirmed'
  | 'declined';
export type AttendeeKind = 'user' | 'staff_contact' | 'client' | 'guest';
export type AttendeeRole = 'host' | 'participant' | 'observer';
export type AttendeeResponse = 'pending' | 'accepted' | 'declined' | 'tentative';
export type ReminderKind =
  | 'created'
  | 'reminder_24h'
  | 'reminder_1h'
  | 'cancelled'
  | 'rescheduled'
  | 'staff_ics'
  | 'confirmed';
export type FeedSubjectKind = 'user' | 'store' | 'org';
export type PublicAction = 'view' | 'confirm' | 'cancel';

export interface CalendarAppointment {
  id: string;
  org_id: string;
  store_id: string | null;
  scope: AppointmentScope;
  assigned_user_id: string | null;
  title: string;
  description: string | null;
  notes: string | null;
  appointment_type: AppointmentType;
  location_text: string | null;
  location_url: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  status: AppointmentStatus;
  confirmation_state: ConfirmationState;
  client_id: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  guest_locale: string | null;
  /**
   * Joined counterparty fields from `calendar.v_appointment_summary`.
   * Populated by `getAppointment` for detail views so they can render
   * the client's current identity/contact info without a second service call.
   */
  client_display_name?: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  source_locale: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  /**
   * Telemetry: who closed the loop on the appointment.
   * `'public_link'` = the recipient tapped on /c/<token>.
   * `'operator'`    = someone with org access flipped via the app.
   * `'sms_inbound'` = bare YES/CANCEL reply matched via sendblue webhook.
   * NULL until first confirmation / cancellation.
   * Added in 20260622010100; AppShell realtime toast filters on
   * `public_link`.
   */
  confirmation_source: string | null;
  cancellation_source: string | null;
  /**
   * Per-appointment instructions surfaced to the recipient on /c/<token>.
   * NULL = use the platform / store-level Public-page template default.
   * Added in 20260622010300_calendar_public_instructions.sql.
   */
  public_instructions: string | null;
  send_email_reminders: boolean;
  send_sms_reminders: boolean;
  reminder_24h_enabled: boolean;
  reminder_1h_enabled: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface CalendarAppointmentSummary {
  id: string;
  org_id: string;
  store_id: string | null;
  scope: AppointmentScope;
  assigned_user_id: string | null;
  title: string;
  appointment_type: AppointmentType;
  location_text: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  status: AppointmentStatus;
  confirmation_state: ConfirmationState;
  client_id: string | null;
  guest_name: string | null;
  client_display_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Joined from `core.stores.name` via the `v_appointment_summary` view
   * (migration `20260614000000_org_mode_aggregates.sql`). Null when the
   * appointment is scope='org' (no store).
   */
  store_name: string | null;
}

export interface CalendarStaffContact {
  id: string;
  org_id: string;
  store_id: string | null;
  display_name: string;
  email: string | null;
  phone: string | null;
  default_timezone: string | null;
  default_locale: string | null;
  notes: string | null;
  linked_user_id: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface CalendarAttendee {
  id: string;
  appointment_id: string;
  org_id: string;
  attendee_kind: AttendeeKind;
  user_id: string | null;
  staff_contact_id: string | null;
  client_id: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  role: AttendeeRole;
  response: AttendeeResponse;
  /** Snapshot fields added in 20260622000100; null on legacy rows. */
  display_name: string | null;
  email: string | null;
  phone: string | null;
  locale: string | null;
  send_email: boolean;
  send_sms: boolean;
  created_at: string;
}

export interface CalendarSettings {
  org_id: string;
  default_appointment_duration_minutes: number;
  reminder_24h_enabled: boolean;
  reminder_1h_enabled: boolean;
  confirmation_required_default: boolean;
  allow_self_service_cancel: boolean;
  default_locale: string | null;
  default_timezone: string | null;
  email_from_name: string | null;
  sms_brand_short: string | null;
  business_hours: Record<string, unknown>;
  extras: Record<string, unknown>;
  updated_at: string;
  updated_by: string | null;
}

export interface CalendarFeedToken {
  token: string;
  org_id: string;
  subject_kind: FeedSubjectKind;
  subject_id: string;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  label: string | null;
}

export interface ListAppointmentsInput extends KeysetInput {
  scope?: AppointmentScope | AppointmentScope[];
  status?: AppointmentStatus | AppointmentStatus[];
  assignedUserId?: string;
  clientId?: string;
  rangeStart?: string;
  rangeEnd?: string;
  search?: string;
  includeArchived?: boolean;
}

export interface CreateAppointmentInput {
  orgId: string;
  storeId?: string | null;
  scope: AppointmentScope;
  assignedUserId?: string | null;
  title: string;
  description?: string | null;
  notes?: string | null;
  appointmentType: AppointmentType;
  locationText?: string | null;
  locationUrl?: string | null;
  startsAt: string;
  endsAt: string;
  timezone?: string;
  clientId?: string | null;
  guestName?: string | null;
  guestEmail?: string | null;
  guestPhone?: string | null;
  guestLocale?: string | null;
  sourceLocale?: string;
  sendEmailReminders?: boolean;
  sendSmsReminders?: boolean;
  reminder24hEnabled?: boolean;
  reminder1hEnabled?: boolean;
  attendees?: AttendeeInput[];
  publicInstructions?: string | null;
}

/**
 * Wire shape passed to `calendar.rpc_create_appointment`'s
 * `attendees` array. Three kinds:
 *
 *   * `staff`  — reference an existing `calendar.staff_contacts` row
 *   * `user`   — reference an existing teammate (auth user in the org)
 *   * `ad_hoc` — free-form attendee with email and/or phone
 *
 * `send_email` / `send_sms` default to "whichever channel we have a
 * value for", but the operator can override per attendee.
 */
export type AttendeeInput =
  | {
      kind: 'staff';
      staff_id: string;
      name?: string;
      locale?: string;
      send_email?: boolean;
      send_sms?: boolean;
    }
  | {
      kind: 'user';
      user_id: string;
      name?: string;
      locale?: string;
      send_email?: boolean;
      send_sms?: boolean;
    }
  | {
      kind: 'ad_hoc';
      name: string;
      email?: string | null;
      phone?: string | null;
      locale?: string;
      send_email?: boolean;
      send_sms?: boolean;
    };

export interface OrgTeammate {
  user_id: string;
  display_name: string;
  email: string | null;
  phone: string | null;
  locale: string;
}

// ---- schema accessors ---------------------------------------------------

function calSchema() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (supabase() as any).schema('calendar');
}

// =========================================================================
// Agenda list (paginated)
// =========================================================================

/**
 * Keyset-paginated appointment agenda.
 *
 * Defaults: future-first sort (starts_at ascending) within the active org.
 * When `storeId` is set in ActiveContext we scope to that store; otherwise
 * we show all org-wide appointments the operator can see.
 */
export async function listAppointments(
  ctx: ActiveContext,
  input: ListAppointmentsInput = {},
): Promise<ListResult<CalendarAppointmentSummary>> {
  const { orgId, storeId } = requireOrgContext(ctx);
  const pageSize = clampPageSize(input.pageSize);

  let q = calSchema()
    .from('v_appointment_summary')
    .select('*', selectCountOption(input.countMode))
    .eq('org_id', orgId);

  if (storeId) {
    q = q.or(`store_id.is.null,store_id.eq.${storeId}`);
  }
  if (Array.isArray(input.scope) && input.scope.length > 0) {
    q = q.in('scope', input.scope);
  } else if (typeof input.scope === 'string') {
    q = q.eq('scope', input.scope);
  }
  if (Array.isArray(input.status) && input.status.length > 0) {
    q = q.in('status', input.status);
  } else if (typeof input.status === 'string') {
    q = q.eq('status', input.status);
  }
  if (input.assignedUserId) q = q.eq('assigned_user_id', input.assignedUserId);
  if (input.clientId) q = q.eq('client_id', input.clientId);
  if (input.rangeStart) q = q.gte('starts_at', input.rangeStart);
  if (input.rangeEnd) q = q.lte('starts_at', input.rangeEnd);
  if (input.search && input.search.trim().length > 0) {
    const s = `%${input.search.trim()}%`;
    q = q.or(
      `title.ilike.${s},client_display_name.ilike.${s},guest_name.ilike.${s}`,
    );
  }
  if (!input.includeArchived) {
    q = q.is('archived_at', null);
  }

  q = applyKeyset(q, input.cursor, 'starts_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as CalendarAppointmentSummary[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'starts_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

/**
 * Bounded list for dashboard widgets / day-glance.
 */
export async function listUpcomingAppointments(
  ctx: ActiveContext,
  limit = 20,
): Promise<CalendarAppointmentSummary[]> {
  const { orgId, storeId } = requireOrgContext(ctx);
  let q = calSchema()
    .from('v_appointment_summary')
    .select('*')
    .eq('org_id', orgId)
    .gte('starts_at', new Date().toISOString())
    .is('archived_at', null)
    .not('status', 'in', '(cancelled,completed,no_show)');
  if (storeId) {
    q = q.or(`store_id.is.null,store_id.eq.${storeId}`);
  }
  q = q.order('starts_at', { ascending: true }).limit(Math.min(limit, 50));
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CalendarAppointmentSummary[];
}

// =========================================================================
// Get
// =========================================================================

export async function getAppointment(
  _ctx: ActiveContext,
  id: string,
): Promise<CalendarAppointment | null> {
  const { data, error } = await calSchema()
    .from('appointments')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const { data: summary, error: summaryErr } = await calSchema()
    .from('v_appointment_summary')
    .select('client_display_name, client_email, client_phone')
    .eq('id', id)
    .maybeSingle();
  if (summaryErr) throw summaryErr;

  return {
    ...(data as CalendarAppointment),
    client_display_name: (summary as CalendarAppointmentSummary | null)?.client_display_name ?? null,
    client_email: (summary as CalendarAppointmentSummary | null)?.client_email ?? null,
    client_phone: (summary as CalendarAppointmentSummary | null)?.client_phone ?? null,
  };
}

export async function getAppointmentAttendees(
  _ctx: ActiveContext,
  appointmentId: string,
): Promise<CalendarAttendee[]> {
  const { data, error } = await calSchema()
    .from('appointment_attendees')
    .select('*')
    .eq('appointment_id', appointmentId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as CalendarAttendee[];
}

// =========================================================================
// Mutations
// =========================================================================

export async function createAppointment(
  _ctx: ActiveContext,
  input: CreateAppointmentInput,
): Promise<string> {
  const payload: Record<string, unknown> = {
    org_id: input.orgId,
    store_id: input.storeId ?? null,
    scope: input.scope,
    assigned_user_id: input.assignedUserId ?? null,
    title: input.title,
    description: input.description ?? null,
    notes: input.notes ?? null,
    appointment_type: input.appointmentType,
    location_text: input.locationText ?? null,
    location_url: input.locationUrl ?? null,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    timezone: input.timezone ?? 'UTC',
    client_id: input.clientId ?? null,
    guest_name: input.guestName ?? null,
    guest_email: input.guestEmail ?? null,
    guest_phone: input.guestPhone ?? null,
    guest_locale: input.guestLocale ?? null,
    source_locale: input.sourceLocale ?? 'en',
    send_email_reminders: input.sendEmailReminders ?? true,
    send_sms_reminders: input.sendSmsReminders ?? true,
    reminder_24h_enabled: input.reminder24hEnabled ?? true,
    reminder_1h_enabled: input.reminder1hEnabled ?? false,
  };
  if (input.attendees && input.attendees.length > 0) {
    payload.attendees = input.attendees;
  }
  if (input.publicInstructions !== undefined) {
    payload.public_instructions = input.publicInstructions;
  }
  const { data, error } = await calSchema().rpc('rpc_create_appointment', {
    p_input: payload,
  });
  if (error) throw error;
  return data as string;
}

/**
 * Search the active org's teammate roster for the "Invite a teammate"
 * tab of the appointment attendees picker. Backed by
 * `calendar.rpc_list_org_teammates` (migration 20260622000200).
 */
export async function listOrgTeammates(
  ctx: ActiveContext,
  { search, limit }: { search?: string; limit?: number } = {},
): Promise<OrgTeammate[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await calSchema().rpc('rpc_list_org_teammates', {
    p_org_id: orgId,
    p_search: search ?? null,
    p_limit: limit ?? 25,
  });
  if (error) throw error;
  return (data ?? []) as OrgTeammate[];
}

export async function updateAppointment(
  _ctx: ActiveContext,
  id: string,
  patch: Partial<CalendarAppointment>,
): Promise<CalendarAppointment> {
  const { data, error } = await calSchema()
    .from('appointments')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as CalendarAppointment;
}

export async function cancelAppointment(
  _ctx: ActiveContext,
  id: string,
  reason?: string,
): Promise<void> {
  const { error } = await calSchema().rpc('rpc_cancel_appointment', {
    p_id: id,
    p_reason: reason ?? null,
  });
  if (error) throw error;
}

export async function setAppointmentStatus(
  _ctx: ActiveContext,
  id: string,
  status: AppointmentStatus,
): Promise<void> {
  const { error } = await calSchema().rpc('rpc_set_appointment_status', {
    p_id: id,
    p_status: status,
  });
  if (error) throw error;
}

export interface TestReminderResult {
  kind: string;
  email_message_id: string | null;
  sms_message_id: string | null;
  sent_to_email: string | null;
  sent_to_phone: string | null;
}

export async function sendTestAppointmentNotification(
  _ctx: ActiveContext,
  id: string,
  kind: ReminderKind,
): Promise<TestReminderResult> {
  const { data, error } = await calSchema().rpc(
    'rpc_send_test_appointment_notification',
    { p_appointment_id: id, p_kind: kind },
  );
  if (error) throw error;
  return data as TestReminderResult;
}

// =========================================================================
// Staff contacts
// =========================================================================

export async function listStaffContacts(
  ctx: ActiveContext,
  includeArchived = false,
): Promise<CalendarStaffContact[]> {
  const { orgId } = requireOrgContext(ctx);
  let q = calSchema()
    .from('staff_contacts')
    .select('*')
    .eq('org_id', orgId)
    .order('display_name', { ascending: true });
  if (!includeArchived) q = q.is('archived_at', null);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as CalendarStaffContact[];
}

export interface UpsertStaffContactInput {
  id?: string;
  orgId: string;
  storeId?: string | null;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  defaultTimezone?: string | null;
  defaultLocale?: string | null;
  notes?: string | null;
  linkedUserId?: string | null;
}

export async function upsertStaffContact(
  _ctx: ActiveContext,
  input: UpsertStaffContactInput,
): Promise<CalendarStaffContact> {
  const row = {
    id: input.id,
    org_id: input.orgId,
    store_id: input.storeId ?? null,
    display_name: input.displayName,
    email: input.email ?? null,
    phone: input.phone ?? null,
    default_timezone: input.defaultTimezone ?? null,
    default_locale: input.defaultLocale ?? null,
    notes: input.notes ?? null,
    linked_user_id: input.linkedUserId ?? null,
  };
  if (input.id) {
    const { data, error } = await calSchema()
      .from('staff_contacts')
      .update(row)
      .eq('id', input.id)
      .select('*')
      .single();
    if (error) throw error;
    return data as CalendarStaffContact;
  }
  const { data, error } = await calSchema()
    .from('staff_contacts')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as CalendarStaffContact;
}

export async function archiveStaffContact(
  _ctx: ActiveContext,
  id: string,
): Promise<void> {
  const { error } = await calSchema()
    .from('staff_contacts')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// =========================================================================
// Settings
// =========================================================================

export async function getCalendarSettings(
  ctx: ActiveContext,
): Promise<CalendarSettings | null> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await calSchema()
    .from('settings')
    .select('*')
    .eq('org_id', orgId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as CalendarSettings | null;
}

export interface UpsertCalendarSettingsInput {
  orgId: string;
  defaultAppointmentDurationMinutes?: number;
  reminder24hEnabled?: boolean;
  reminder1hEnabled?: boolean;
  confirmationRequiredDefault?: boolean;
  allowSelfServiceCancel?: boolean;
  defaultLocale?: string | null;
  defaultTimezone?: string | null;
  emailFromName?: string | null;
  smsBrandShort?: string | null;
  businessHours?: Record<string, unknown>;
}

export async function upsertCalendarSettings(
  _ctx: ActiveContext,
  input: UpsertCalendarSettingsInput,
): Promise<CalendarSettings> {
  const row: Record<string, unknown> = { org_id: input.orgId };
  if (input.defaultAppointmentDurationMinutes !== undefined)
    row.default_appointment_duration_minutes = input.defaultAppointmentDurationMinutes;
  if (input.reminder24hEnabled !== undefined)
    row.reminder_24h_enabled = input.reminder24hEnabled;
  if (input.reminder1hEnabled !== undefined)
    row.reminder_1h_enabled = input.reminder1hEnabled;
  if (input.confirmationRequiredDefault !== undefined)
    row.confirmation_required_default = input.confirmationRequiredDefault;
  if (input.allowSelfServiceCancel !== undefined)
    row.allow_self_service_cancel = input.allowSelfServiceCancel;
  if (input.defaultLocale !== undefined) row.default_locale = input.defaultLocale;
  if (input.defaultTimezone !== undefined)
    row.default_timezone = input.defaultTimezone;
  if (input.emailFromName !== undefined) row.email_from_name = input.emailFromName;
  if (input.smsBrandShort !== undefined) row.sms_brand_short = input.smsBrandShort;
  if (input.businessHours !== undefined) row.business_hours = input.businessHours;

  const { data, error } = await calSchema()
    .from('settings')
    .upsert(row, { onConflict: 'org_id' })
    .select('*')
    .single();
  if (error) throw error;
  return data as CalendarSettings;
}

// =========================================================================
// Feed tokens
// =========================================================================

export async function mintFeedToken(
  _ctx: ActiveContext,
  subjectKind: FeedSubjectKind,
  subjectId: string,
  label?: string,
): Promise<string> {
  const { data, error } = await calSchema().rpc('rpc_mint_feed_token', {
    p_subject_kind: subjectKind,
    p_subject_id: subjectId,
    p_label: label ?? null,
  });
  if (error) throw error;
  return data as string;
}

export async function revokeFeedToken(
  _ctx: ActiveContext,
  token: string,
): Promise<void> {
  const { error } = await calSchema().rpc('rpc_revoke_feed_token', {
    p_token: token,
  });
  if (error) throw error;
}

export async function listFeedTokens(
  ctx: ActiveContext,
): Promise<CalendarFeedToken[]> {
  const { orgId } = requireOrgContext(ctx);
  const { data, error } = await calSchema()
    .from('feed_tokens')
    .select('*')
    .eq('org_id', orgId)
    .is('revoked_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as CalendarFeedToken[];
}

export async function mintPublicActionToken(
  _ctx: ActiveContext,
  appointmentId: string,
  action: PublicAction = 'view',
): Promise<string> {
  const { data, error } = await calSchema().rpc('rpc_mint_public_action_token', {
    p_appointment_id: appointmentId,
    p_action: action,
  });
  if (error) throw error;
  return data as string;
}

// =========================================================================
// Realtime
// =========================================================================

/**
 * Shape of the `payload.new` row pushed by the supabase_realtime
 * publication on `calendar.appointments` UPDATE events. We only care
 * about a narrow subset for the AppShell toast (status transitions
 * triggered by the public /c/<token> link), so we keep this DTO local
 * rather than re-using the heavy `CalendarAppointment` interface.
 */
export interface AppointmentRealtimeRow {
  id: string;
  org_id: string;
  store_id: string | null;
  title: string;
  status: AppointmentStatus;
  confirmation_state: ConfirmationState;
  confirmation_source: string | null;
  cancellation_source: string | null;
  starts_at: string;
  timezone: string;
}

/**
 * Subscribe to UPDATE events on `calendar.appointments` scoped to a
 * single `org_id`. The Supabase JS client requires this filter to be
 * expressed as a PostgREST-style string ("org_id=eq.<uuid>").
 *
 * Pairs with the `supabase_realtime` publication entry added in
 * `20260622010100_calendar_confirmed_fanout_and_sources.sql`.
 *
 * The callback fires once per UPDATE; the consumer is responsible for
 * filtering down to the "public_link" transitions it cares about by
 * inspecting `next.confirmation_source` / `next.cancellation_source`.
 */
export function subscribeToOrgAppointments(
  orgId: string,
  onChange: (next: AppointmentRealtimeRow) => void,
): { unsubscribe: () => void } {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase() as any;
  const channel = sb
    .channel(`calendar_appointments:org:${orgId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'calendar',
        table: 'appointments',
        filter: `org_id=eq.${orgId}`,
      },
      (payload: { new: AppointmentRealtimeRow }) => {
        if (payload?.new) onChange(payload.new);
      },
    )
    .subscribe();
  return {
    unsubscribe: () => {
      try {
        sb.removeChannel(channel);
      } catch {
        // ignore
      }
    },
  };
}
